import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Stethoscope, Eye, EyeOff } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
            {/* Left: form */}
            <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
                <div className="w-full max-w-md">
                    <div className="flex items-center gap-3 mb-12">
                        <div className="h-11 w-11 rounded-lg bg-brand text-white flex items-center justify-center">
                            <Stethoscope className="h-6 w-6" strokeWidth={1.5} />
                        </div>
                        <div className="leading-tight">
                            <div className="font-heading font-semibold text-slate-900">БГМК · 2026</div>
                            <div className="text-xs text-slate-500">Приёмная комиссия</div>
                        </div>
                    </div>

                    <h1 className="text-3xl lg:text-4xl font-heading font-medium tracking-tight text-slate-900 mb-3">
                        Вход в кабинет
                    </h1>
                    <p className="text-slate-500 mb-10 leading-relaxed">
                        Войдите, чтобы отвечать на обращения абитуриентов и управлять контентом бота.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@bsmc.by"
                                data-testid="login-email-input"
                                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white placeholder:text-slate-400 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                Пароль
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    data-testid="login-password-input"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2.5 pr-10 text-sm bg-white placeholder:text-slate-400 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    data-testid="login-toggle-password"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div
                                className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2.5"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="w-full bg-brand text-white hover:bg-brand-hover disabled:opacity-60 transition-colors duration-200 rounded-md px-4 py-2.5 font-medium text-sm"
                        >
                            {submitting ? "Вход..." : "Войти"}
                        </button>
                    </form>

                    <p className="text-xs text-slate-400 mt-10 leading-relaxed">
                        Доступ предоставляется администратором. Если у вас нет учётной записи — обратитесь к
                        председателю приёмной комиссии.
                    </p>
                </div>
            </div>

            {/* Right: image */}
            <div className="hidden lg:block relative">
                <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/50" />
                <div className="relative h-full flex items-end p-12 text-white">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] opacity-70 mb-3">БГМК · 2026</div>
                        <div className="text-3xl font-heading font-medium leading-tight max-w-md">
                            Кабинет приёмной комиссии для оперативной работы с обращениями абитуриентов.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
